import { EVINCED_SEVERITIES_IDS, SORT_ORDER } from '@evinced-private/ui-common';

import { ProductType } from 'src/common/interfaces/Tenant';

import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '../MobileScansList.constants';
import {
	IGetMobileScansListParams,
	IMobileScansListFallbackIssue,
	IMobileScansListFallbackQuery,
	IMobileScansListFallbackScan,
	IMobileScansListResult,
	IMobileScansListRow,
	MobileScansProductType
} from '../MobileScansList.types';

const EMPTY_SUMMARY = {
	scansCount: 0,
	issuesCount: 0,
	elementsWithIssuesCount: 0
};

interface FallbackScanAccumulator {
	scanId: string;
	scanName: string;
	runId: string;
	productName: MobileScansProductType;
	scanScreenshotUrl: string;
	totalIssues: number;
	criticalIssues: number;
	seriousIssues: number;
	elementIds: Set<string>;
}

const isFallbackIssue = (
	issue: IMobileScansListFallbackIssue | null
): issue is IMobileScansListFallbackIssue => Boolean(issue?.scan_id);

const isFallbackScan = (
	scan: IMobileScansListFallbackScan | null
): scan is IMobileScansListFallbackScan => Boolean(scan?.scan_id);

const isNonEmptyString = (value: string | null | undefined): value is string => Boolean(value);

const getFallbackProductName = (
	paramsProductName: MobileScansProductType | undefined,
	issueProductName: MobileScansProductType | null | undefined
): MobileScansProductType =>
	paramsProductName ?? issueProductName ?? ProductType.MOBILE_FLOW_ANALYZER;

const createEmptyAccumulator = (
	scanId: string,
	scanName: string,
	runId: string,
	productName: MobileScansProductType
): FallbackScanAccumulator => ({
	scanId,
	scanName,
	runId,
	productName,
	scanScreenshotUrl: '',
	totalIssues: 0,
	criticalIssues: 0,
	seriousIssues: 0,
	elementIds: new Set<string>()
});

const createAccumulator = (
	issue: IMobileScansListFallbackIssue,
	productName?: MobileScansProductType
): FallbackScanAccumulator => ({
	...createEmptyAccumulator(
		issue.scan_id,
		issue.scan_name || issue.scan_id,
		issue.run_id,
		getFallbackProductName(productName, issue.product_name)
	),
	scanScreenshotUrl: issue.screenshot ?? ''
});

const mutateAccumulatorWithIssue = (
	accumulator: FallbackScanAccumulator,
	issue: IMobileScansListFallbackIssue
): void => {
	accumulator.totalIssues += 1;

	if (issue.severity_name === EVINCED_SEVERITIES_IDS.CRITICAL) {
		accumulator.criticalIssues += 1;
	}

	if (issue.severity_name === EVINCED_SEVERITIES_IDS.SERIOUS) {
		accumulator.seriousIssues += 1;
	}

	if (!accumulator.scanScreenshotUrl && issue.screenshot) {
		accumulator.scanScreenshotUrl = issue.screenshot;
	}

	issue.elements__id?.filter(isNonEmptyString).forEach((elementId) => {
		accumulator.elementIds.add(elementId);
	});
};

const buildScanCreatedAtMap = (
	scans: Array<IMobileScansListFallbackScan | null> | null | undefined
): Map<string, string> => {
	const scanCreatedAtMap = new Map<string, string>();
	scans?.filter(isFallbackScan).forEach((scan) => {
		scanCreatedAtMap.set(scan.scan_id, scan.created_at ?? '');
	});
	return scanCreatedAtMap;
};

const toRow = (
	accumulator: FallbackScanAccumulator,
	scanCreatedAtMap: Map<string, string>
): IMobileScansListRow => ({
	productName: accumulator.productName,
	runId: accumulator.runId,
	scanId: accumulator.scanId,
	scanName: accumulator.scanName,
	scanCreatedAt: scanCreatedAtMap.get(accumulator.scanId) ?? '',
	scanScreenshotUrl: accumulator.scanScreenshotUrl,
	totalIssues: accumulator.totalIssues,
	criticalIssues: accumulator.criticalIssues,
	seriousIssues: accumulator.seriousIssues,
	elementsWithIssues: accumulator.elementIds.size
});

const getSortValue = (row: IMobileScansListRow, dataField: string): string | number | undefined =>
	row[dataField as keyof IMobileScansListRow] as string | number | undefined;

const sortRows = (
	rows: IMobileScansListRow[],
	params: IGetMobileScansListParams
): IMobileScansListRow[] => {
	const { sort } = params;
	const dataField = sort?.dataField;
	const orderMultiplier = sort?.order === SORT_ORDER.DESC ? -1 : 1;

	if (!dataField) {
		return rows;
	}

	return [...rows].sort((firstRow, secondRow) => {
		const firstValue = getSortValue(firstRow, dataField);
		const secondValue = getSortValue(secondRow, dataField);

		if (typeof firstValue === 'number' && typeof secondValue === 'number') {
			return (firstValue - secondValue) * orderMultiplier;
		}

		return (
			String(firstValue ?? '').localeCompare(String(secondValue ?? ''), undefined, {
				numeric: true,
				sensitivity: 'base'
			}) * orderMultiplier
		);
	});
};

const paginateRows = (
	rows: IMobileScansListRow[],
	{ page = DEFAULT_PAGE, pageSize = DEFAULT_PAGE_SIZE }: IGetMobileScansListParams
): IMobileScansListRow[] => {
	const offset = (page - 1) * pageSize;
	return rows.slice(offset, offset + pageSize);
};

export const transformMobileScansListFallbackResults = (
	rawResults: IMobileScansListFallbackQuery,
	params: IGetMobileScansListParams
): IMobileScansListResult => {
	const scanCreatedAtMap = buildScanCreatedAtMap(rawResults.scans);
	const scanAccumulators = new Map<string, FallbackScanAccumulator>();

	rawResults.issues?.filter(isFallbackIssue).forEach((issue) => {
		const accumulator =
			scanAccumulators.get(issue.scan_id) ?? createAccumulator(issue, params.productName);
		mutateAccumulatorWithIssue(accumulator, issue);
		scanAccumulators.set(issue.scan_id, accumulator);
	});

	if (params.includeScansWithoutIssues) {
		rawResults.scans?.filter(isFallbackScan).forEach((scan) => {
			if (!scanAccumulators.has(scan.scan_id)) {
				scanAccumulators.set(
					scan.scan_id,
					createEmptyAccumulator(
						scan.scan_id,
						scan.scan_id,
						params.runId,
						getFallbackProductName(params.productName, undefined)
					)
				);
			}
		});
	}

	const allRows = Array.from(scanAccumulators.values()).map((accumulator) =>
		toRow(accumulator, scanCreatedAtMap)
	);
	const sortedRows = sortRows(allRows, params);
	const rows = paginateRows(sortedRows, params);
	const summary = allRows.length
		? {
				scansCount: allRows.length,
				issuesCount: allRows.reduce((sum, row) => sum + row.totalIssues, 0),
				elementsWithIssuesCount: allRows.reduce((sum, row) => sum + row.elementsWithIssues, 0)
			}
		: EMPTY_SUMMARY;

	return {
		rows,
		total: allRows.length,
		summary
	};
};
